import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, toApiError } from '@/lib/api'
import { useAnswerApi } from '@/hooks/useAnswerApi'
import { ChatStreamError, streamAnswer } from '@/lib/sse'
import { messagesToTurns } from '@/lib/transcript'
import type { Turn } from '@/lib/transcript'

export type { Turn }

export type HistoryState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }

export type UseChatTranscriptOptions = {
  baseUrl: string
  acquireToken: () => Promise<string>
  fetchImpl?: typeof fetch
  /** Fires when a turn has been persisted server-side (StreamDone) — the
   * conversation's updated_at just bumped, so refresh any list of them. */
  onTurnPersisted?: (conversationId: string) => void
}

export type UseChatTranscriptResult = {
  turns: Turn[]
  conversationId: string | null
  historyState: HistoryState
  isStreaming: boolean
  ask: (query: string) => Promise<void>
  openConversation: (id: string) => Promise<void>
  reset: () => void
}

export function useChatTranscript(options: UseChatTranscriptOptions): UseChatTranscriptResult {
  const { onTurnPersisted } = options
  const [turns, setTurns] = useState<Turn[]>([])
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [historyState, setHistoryState] = useState<HistoryState>({ kind: 'idle' })
  const abortRef = useRef<AbortController | null>(null)
  // `ask` reads the active id through a ref so adoption from StreamStart is
  // visible to a send fired before React re-renders with the new state.
  const conversationIdRef = useRef<string | null>(null)

  const api = useAnswerApi(options)

  useEffect(
    () => () => {
      abortRef.current?.abort()
    },
    [],
  )

  const adoptConversationId = useCallback((id: string) => {
    if (id === '') return
    conversationIdRef.current = id
    setConversationId(id)
  }, [])

  const ask = useCallback(
    async (query: string) => {
      const trimmed = query.trim()
      if (trimmed === '') return
      const controller = new AbortController()
      abortRef.current?.abort()
      abortRef.current = controller

      const id = makeTurnId()
      setTurns((prev) => [
        ...prev,
        { id, query: trimmed, status: 'streaming', partialText: '', statusText: '' },
      ])

      const replace = (next: Turn) => {
        setTurns((prev) => prev.map((t) => (t.id === id ? next : t)))
      }

      let partial = ''
      let statusText = ''
      try {
        const stream = streamAnswer(
          { query: trimmed, conversationId: conversationIdRef.current },
          {
            baseUrl: options.baseUrl,
            acquireToken: options.acquireToken,
            signal: controller.signal,
            ...(options.fetchImpl !== undefined ? { fetchImpl: options.fetchImpl } : {}),
          },
        )
        for await (const event of stream) {
          if (controller.signal.aborted) return
          if (event.type === 'start') {
            // On a fresh chat the backend creates the conversation and hands
            // the real id back here; every later turn must echo it.
            adoptConversationId(event.conversationId)
          } else if (event.type === 'status') {
            statusText = event.text
            // Reset accumulated text when the server signals a regenerate;
            // chunks from the previous attempt are stale.
            if (event.phase === 'regenerate') partial = ''
            replace({ id, query: trimmed, status: 'streaming', partialText: partial, statusText })
          } else if (event.type === 'chunk') {
            partial += event.content
            replace({ id, query: trimmed, status: 'streaming', partialText: partial, statusText })
          } else if (event.type === 'done') {
            adoptConversationId(event.conversationId)
            replace({ id, query: trimmed, status: 'ready', result: event.result })
            const persistedId = event.conversationId || conversationIdRef.current
            if (persistedId !== null && persistedId !== '') {
              onTurnPersisted?.(persistedId)
            }
            return
          } else if (event.type === 'error') {
            replace({
              id,
              query: trimmed,
              status: 'error',
              message: event.message,
              httpStatus: null,
            })
            return
          }
        }
      } catch (err) {
        if (controller.signal.aborted) return
        let message = 'Unknown error'
        let httpStatus: number | null = null
        if (err instanceof ChatStreamError) {
          message = err.message
          httpStatus = err.status
        } else if (err instanceof Error) {
          message = err.message
        }
        replace({ id, query: trimmed, status: 'error', message, httpStatus })
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null
        }
      }
    },
    [
      options.baseUrl,
      options.acquireToken,
      options.fetchImpl,
      onTurnPersisted,
      adoptConversationId,
    ],
  )

  const openConversation = useCallback(
    async (id: string) => {
      const controller = new AbortController()
      abortRef.current?.abort()
      abortRef.current = controller

      conversationIdRef.current = id
      setConversationId(id)
      setTurns([])
      setHistoryState({ kind: 'loading' })
      try {
        const messages = await api.conversationMessages(
          { conversationId: id },
          { signal: controller.signal },
        )
        if (controller.signal.aborted) return
        setTurns(messagesToTurns(messages))
        setHistoryState({ kind: 'idle' })
      } catch (err) {
        if (controller.signal.aborted) return
        const apiErr: ApiError = err instanceof ApiError ? err : await toApiError(err)
        setHistoryState({ kind: 'error', message: apiErr.message })
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null
        }
      }
    },
    [api],
  )

  const reset = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    conversationIdRef.current = null
    setConversationId(null)
    setTurns([])
    setHistoryState({ kind: 'idle' })
  }, [])

  const isStreaming = useMemo(() => turns.some((t) => t.status === 'streaming'), [turns])

  return { turns, conversationId, historyState, isStreaming, ask, openConversation, reset }
}

function makeTurnId(): string {
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}
